import React from "react";
import { Link } from "react-router-dom";
import { Printer, Palette, Sparkles, Heart, Star, Instagram, MessageCircle, MapPin, Truck, Clock, Package, Store } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { imgUrl, waLink } from "@/lib/api";
import ProductImage from "@/components/ProductImage";
import { OctopusMascot } from "@/components/Mascot";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const WHY_ICONS = { printer: Printer, palette: Palette, sparkles: Sparkles, heart: Heart, star: Star, package: Package };
const WHY_BG = ["from-pink-500 to-rose-400", "from-purple-500 to-violet-400", "from-cyan-500 to-sky-400", "from-fuchsia-500 to-pink-400"];

export function WhyChoose() {
  const { content } = useStore();
  const sec = content?.why_choose || {};
  const items = sec.items || [];
  return (
    <section className="py-16 sm:py-24 bg-soft-gradient" data-testid="why-choose-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 text-center mb-12">{sec.title || "Por que escolher a Art3D?"}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {items.map((item, i) => {
            const Icon = WHY_ICONS[item.icon] || Sparkles;
            return (
              <div key={i} className="bg-white rounded-2xl p-6 shadow-card transition-all duration-300 hover:-translate-y-1.5 hover:shadow-hover-pink" data-testid={`why-card-${i}`}>
                <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${WHY_BG[i % WHY_BG.length]} flex items-center justify-center mb-4`}>
                  <Icon size={22} className="text-white" />
                </div>
                <h3 className="font-bold text-slate-800 text-lg">{item.title}</h3>
                <p className="text-sm text-slate-500 mt-2 leading-relaxed">{item.text}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function GallerySection() {
  const { content } = useStore();
  const sec = content?.gallery || {};
  const images = sec.images || [];
  const slots = images.length ? images : Array(6).fill(null);
  return (
    <section className="py-16 sm:py-24" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">{sec.title || "Galeria"}</h2>
          {sec.subtitle && <p className="mt-2 text-slate-500">{sec.subtitle}</p>}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
          {slots.map((img, i) => (
            <div key={i} className={`rounded-2xl overflow-hidden shadow-card group ${i % 5 === 0 ? "row-span-2 aspect-[3/4] sm:aspect-auto" : "aspect-square"}`} data-testid={`gallery-item-${i}`}>
              {img ? (
                <img src={imgUrl(img)} alt={`Galeria Art3D ${i + 1}`} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
              ) : (
                <ProductImage src="" label="Foto da galeria" className="transition-transform duration-500 group-hover:scale-105" />
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function AboutSection() {
  const { content } = useStore();
  const sec = content?.about || {};
  return (
    <section id="sobre" className="py-16 sm:py-24" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center bg-soft-gradient rounded-[2.5rem] p-8 sm:p-14 shadow-card">
          <div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900" data-testid="about-title">
              {sec.title || "Conheça a Art3D 💗"}
            </h2>
            <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed whitespace-pre-line" data-testid="about-text">
              {sec.text || "A Art3D nasceu para transformar a impressão 3D em peças criativas, divertidas e úteis para o dia a dia."}
            </p>
          </div>
          <div className="relative">
            <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-card bg-white">
              {sec.image ? (
                <img src={imgUrl(sec.image)} alt="Sobre a Art3D" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-soft-gradient flex flex-col items-center justify-center gap-3">
                  <OctopusMascot size={120} className="animate-float-slow" />
                  <span className="text-sm font-semibold text-slate-400">Espaço para a foto da marca</span>
                </div>
              )}
            </div>
            <div className="absolute -bottom-4 -right-3 sm:-right-4">
              <OctopusMascot size={64} wave />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function HowToBuy() {
  const { content } = useStore();
  const sec = content?.how_to_buy || {};
  const steps = sec.steps || [];
  return (
    <section className="py-16 sm:py-24" data-testid="how-to-buy-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 text-center mb-12">{sec.title || "Como comprar"}</h2>
        <div className="grid sm:grid-cols-3 gap-6">
          {steps.map((step, i) => (
            <div key={i} className="relative bg-white rounded-2xl p-6 pt-10 shadow-card text-center transition-all duration-300 hover:-translate-y-1.5" data-testid={`how-step-${i}`}>
              <span className="absolute -top-5 left-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-brand-gradient text-white font-black flex items-center justify-center shadow-soft">
                {i + 1}
              </span>
              <h3 className="font-bold text-slate-800">{step.title}</h3>
              <p className="text-sm text-slate-500 mt-2 leading-relaxed">{step.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function OrderSteps() {
  const { content } = useStore();
  const sec = content?.order_steps || {};
  const steps = sec.steps || [];
  if (!steps.length) return null;
  return (
    <section className="py-16 sm:py-24 bg-soft-gradient" data-testid="order-steps-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 text-center mb-12">
          📦 {sec.title || "Como funciona seu pedido?"}
        </h2>
        <div className="relative flex flex-col gap-6">
          <div className="absolute left-5 top-2 bottom-2 w-0.5 bg-gradient-to-b from-pink-300 via-purple-300 to-cyan-300" />
          {steps.map((step, i) => (
            <div key={i} className="relative flex gap-5 items-start" data-testid={`order-step-${i}`}>
              <span className="relative z-10 w-10 h-10 shrink-0 rounded-full bg-white shadow-card border-2 border-pink-200 font-black text-pink-600 flex items-center justify-center">
                {i + 1}
              </span>
              <div className="bg-white rounded-2xl shadow-card p-5 flex-1">
                <h3 className="font-bold text-slate-800">{step.title}</h3>
                <p className="text-sm text-slate-500 mt-1">{step.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function DeliverySection() {
  const { content } = useStore();
  const sec = content?.delivery || {};
  const rows = [
    { icon: MapPin, label: "Regiões atendidas", value: sec.regions },
    { icon: Truck, label: "Formas de entrega", value: sec.methods },
    { icon: Clock, label: "Prazo", value: sec.deadline },
    { icon: Package, label: "Taxa de entrega", value: sec.fee },
  ].filter((r) => r.value);
  return (
    <section className="py-16 sm:py-24" data-testid="delivery-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <span className="inline-flex items-center gap-2 bg-pink-50 text-pink-600 text-sm font-bold px-4 py-2 rounded-full mb-4">
            <Store size={16} /> {sec.subtitle || "Entregamos em Natal e região"}
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">{sec.title || "Entrega e retirada"}</h2>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {rows.map((row, i) => (
            <div key={i} className="bg-white rounded-2xl shadow-card p-5 flex items-start gap-4" data-testid={`delivery-row-${i}`}>
              <span className="w-11 h-11 shrink-0 rounded-xl bg-gradient-to-br from-pink-100 to-purple-100 flex items-center justify-center">
                <row.icon size={20} className="text-pink-600" />
              </span>
              <div>
                <h3 className="font-bold text-slate-800 text-sm">{row.label}</h3>
                <p className="text-sm text-slate-500 mt-1">{row.value}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function TestimonialsSection() {
  const { content } = useStore();
  const sec = content?.testimonials || {};
  const items = sec.items || [];
  if (!items.length) return null;
  return (
    <section className="py-16 sm:py-24 bg-soft-gradient" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 text-center mb-12">{sec.title || "O que dizem nossos clientes"}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((t, i) => (
            <div key={i} className="bg-white rounded-2xl shadow-card p-6 transition-all duration-300 hover:-translate-y-1.5" data-testid={`testimonial-${i}`}>
              <div className="flex gap-0.5 mb-3">
                {Array.from({ length: Math.max(1, Math.min(5, t.rating || 5)) }).map((_, s) => (
                  <Star key={s} size={16} className="text-yellow-400 fill-yellow-400" />
                ))}
              </div>
              <p className="text-slate-600 text-sm leading-relaxed">“{t.text}”</p>
              <p className="mt-4 font-bold text-slate-800 text-sm">{t.name}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function InstagramSection() {
  const { content } = useStore();
  const sec = content?.instagram || {};
  const images = sec.images || [];
  const slots = images.length ? images : Array(6).fill(null);
  return (
    <section className="py-16 sm:py-24" data-testid="instagram-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <span className="inline-flex w-14 h-14 rounded-2xl bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-500 items-center justify-center mb-5 shadow-soft">
          <Instagram size={26} className="text-white" />
        </span>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">{sec.title || "Veja mais criações da Art3D ✨"}</h2>
        <p className="mt-2 text-lg font-bold text-gradient" data-testid="instagram-handle">{sec.handle || "@art3dd_"}</p>
        <a
          href={content?.contact?.instagram_url || "https://instagram.com/art3dd_"}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="instagram-follow-btn"
          className="inline-flex items-center gap-2 mt-5 bg-brand-gradient text-white font-bold px-7 py-3 rounded-full shadow-soft transition-all duration-200 hover:opacity-90 hover:-translate-y-0.5 active:scale-95"
        >
          <Instagram size={18} />
          {sec.cta || "Seguir no Instagram"}
        </a>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mt-10">
          {slots.map((img, i) => (
            <div key={i} className="aspect-square rounded-2xl overflow-hidden shadow-card group" data-testid={`instagram-tile-${i}`}>
              {img ? (
                <img src={imgUrl(img)} alt={`Post Instagram ${i + 1}`} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
              ) : (
                <ProductImage src="" label="Post" />
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function GiftBanner() {
  const { content } = useStore();
  const sec = content?.gift_banner || {};
  return (
    <section className="py-10 sm:py-14" data-testid="gift-banner-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden rounded-[2.5rem] bg-brand-gradient p-10 sm:p-16 text-center shadow-hover-pink">
          <div className="absolute -top-10 -left-10 w-40 h-40 rounded-full bg-white/10" />
          <div className="absolute -bottom-14 -right-10 w-56 h-56 rounded-full bg-white/10" />
          <div className="relative">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white" data-testid="gift-title">
              {sec.title || "🎁 Procurando um presente diferente?"}
            </h2>
            <p className="mt-3 text-white/90 text-base sm:text-lg max-w-xl mx-auto">{sec.text}</p>
            <Link
              to="/produtos"
              data-testid="gift-cta"
              className="inline-flex items-center gap-2 mt-7 bg-white text-pink-600 font-bold px-7 py-3.5 rounded-full shadow-soft transition-all duration-200 hover:-translate-y-0.5 active:scale-95"
            >
              {sec.cta || "Ver produtos"}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export function FaqSection() {
  const { content } = useStore();
  const sec = content?.faq || {};
  const items = sec.items || [];
  if (!items.length) return null;
  return (
    <section className="py-16 sm:py-24" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center gap-4 mb-10">
          <OctopusMascot size={56} wave className="hidden sm:block" />
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 text-center">{sec.title || "Dúvidas frequentes"}</h2>
        </div>
        <Accordion type="single" collapsible className="flex flex-col gap-3">
          {items.map((item, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="bg-white rounded-2xl shadow-card border-none px-5" data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="text-left font-bold text-slate-800 hover:no-underline py-5">{item.q}</AccordionTrigger>
              <AccordionContent className="text-slate-500 leading-relaxed pb-5">{item.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

export function WhatsAppCTA() {
  const { content } = useStore();
  const sec = content?.whatsapp_cta || {};
  return (
    <section className="py-14 sm:py-20" data-testid="whatsapp-cta-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden rounded-[2.5rem] bg-gradient-to-br from-[#eafff3] to-[#d7fbe8] p-10 sm:p-16 text-center shadow-card">
          <div className="absolute top-6 right-8 hidden sm:block">
            <OctopusMascot size={70} wave />
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900" data-testid="whatsapp-cta-title">
            {sec.title || "Gostou de algum produto?"}
          </h2>
          <p className="mt-3 text-slate-600 text-base sm:text-lg">{sec.text || "Fale com a gente pelo WhatsApp!"}</p>
          <p className="mt-1 font-bold text-[#1da851]" data-testid="whatsapp-cta-number">
            {content?.contact?.whatsapp_display || "(84) 99467-7862"}
          </p>
          <a
            href={waLink(content, "Olá, Art3D! Gostei de um produto e quero mais informações.")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="whatsapp-cta-btn"
            className="inline-flex items-center gap-2 mt-7 bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold px-8 py-4 rounded-full shadow-soft transition-all duration-200 hover:-translate-y-0.5 active:scale-95"
          >
            <MessageCircle size={20} />
            {sec.cta || "Chamar no WhatsApp"}
          </a>
        </div>
      </div>
    </section>
  );
}
